import useProject from '@API/project/useProject';
import {
  DashboardView,
  ProjectTemplate
} from '@experiment-management-shared/types';
import { fromDashboardToTemplate } from '@experiment-management-shared/utils/EMView';
import useChangeWorkspaceViewMessage from '@shared/hooks/useChangeWorkspaceViewMessage';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import { useDashboardApiContext } from '../useDashboardAPIContext';

const useDashboardAutosaveView = () => {
  const { data: project } = useProject();
  const showChangeWorkspaceViewMessage = useChangeWorkspaceViewMessage();

  const {
    saveTemplateOnServerDebounced,
    saveTemplateLocally
  } = useDashboardApiContext();

  return useMemo(() => {
    return debounce((dashboardView: DashboardView) => {
      const dashboardTemplate = fromDashboardToTemplate({
        dashboard: dashboardView,
        project
      });

      if (
        dashboardView.projectId &&
        dashboardView.projectId !== project.projectId
      ) {
        showChangeWorkspaceViewMessage(dashboardTemplate.templateName);
        return;
      }

      const templateToSave = dashboardView.isPersonalView
        ? toPersonalView(dashboardTemplate)
        : { ...dashboardTemplate, unsavedView: false };

      saveTemplateLocally(templateToSave);
      saveTemplateOnServerDebounced({ view: templateToSave });
    }, 100);
  }, [
    project,
    saveTemplateLocally,
    saveTemplateOnServerDebounced,
    showChangeWorkspaceViewMessage
  ]);
};

function toPersonalView(template: ProjectTemplate): ProjectTemplate {
  return {
    ...template,
    v3: {
      ...template.v3,
      isPersonalView: true
    },
    createdFromTemplateId: null,
    templateId: null,
    unsavedView: true
  };
}

export default useDashboardAutosaveView;
